"use client"

import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer"
import type * as React from "react"

import { cn } from "@/lib/utils"

// OKS-1022 — bottom sheet on Base UI's Drawer (was vaul, i.e. Radix Dialog).
//
// Why the port: Radix's modal writes `pointer-events: none` on <body>, so any
// Base UI popup portalled to <body> from inside a sheet painted but could not
// be tapped (OKS-1003). Base UI's `Drawer.Portal` IS `Dialog.Portal`, a
// `FloatingPortal` that provides `PortalContext`, so a Select/Popover/Menu
// rendered inside the sheet portals INTO the sheet's portal node — no
// `container` plumbing — and nothing here writes `body.style.pointerEvents`.
//
// Contract kept from the vaul build (specs select on these):
//   - every `drawer-*` data-slot name. `data-slot="drawer"` sits on Root, which
//     renders no element (as under vaul), so it never reaches the DOM;
//     `drawer-portal` is now a real <div>.
//   - `drawer-overlay` keeps `bg-black/50`: the OKS-568 blur rule
//     (`[data-slot$="-overlay"]` in application.css) and the translucency spec
//     read the colour alpha, so any fade must use `opacity`, never the colour.
//
// Deliberate behaviour changes (PRD OKS-1022 §3):
//   - focus moves onto the sheet container on open. `DrawerPopup` defaults
//     `initialFocus` to the popup itself, so do NOT pass `initialFocus` —
//     "first tabbable" would pop the phone keyboard on sheets with a field.
//   - close focus is `finalFocus` (Base UI), not Radix's `onCloseAutoFocus`.
//
// Structure: Portal › Backdrop + Viewport › Popup. The Viewport is required —
// without it Base UI disables swipe handling and touch scroll-locking. Every
// Crew sheet is a bottom sheet (`swipeDirection="down"`, the default).

function Drawer({ ...props }: DrawerPrimitive.Root.Props) {
  return <DrawerPrimitive.Root data-slot="drawer" {...props} />
}

function DrawerTrigger({ ...props }: DrawerPrimitive.Trigger.Props) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />
}

function DrawerPortal({ ...props }: DrawerPrimitive.Portal.Props) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />
}

function DrawerClose({ ...props }: DrawerPrimitive.Close.Props) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />
}

function DrawerOverlay({
  className,
  ...props
}: DrawerPrimitive.Backdrop.Props) {
  return (
    <DrawerPrimitive.Backdrop
      data-slot="drawer-overlay"
      className={cn(
        "data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className,
      )}
      {...props}
    />
  )
}

function DrawerContent({
  className,
  children,
  showHandle = true,
  handle,
  ...props
}: DrawerPrimitive.Popup.Props & {
  /**
   * Render the generic grab bar at the top of the sheet (default). A sheet
   * that draws its own cue passes `false` (or a `handle`) so there are not
   * two bars.
   */
  showHandle?: boolean
  /**
   * A sheet's own drag cue / chrome, rendered OUTSIDE the content wrapper so
   * a mouse press on it can still start a swipe (see the content note below).
   * Replaces the generic grab bar.
   */
  handle?: React.ReactNode
}) {
  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DrawerPrimitive.Viewport
        data-slot="drawer-viewport"
        className="fixed inset-0 z-50"
      >
        <DrawerPrimitive.Popup
          data-slot="drawer-content"
          className={cn(
            // Geometry keeps the vaul build's attribute-variant form on purpose:
            // `data-[swipe-direction=down]:*` has the same specificity as the
            // old `data-[vaul-drawer-direction=bottom]:*`, so a consumer
            // className that lost (or won) the cascade before still does.
            "group/drawer-content bg-background fixed z-50 flex h-auto flex-col outline-none",
            "data-[swipe-direction=down]:inset-x-0 data-[swipe-direction=down]:bottom-0 data-[swipe-direction=down]:mt-24 data-[swipe-direction=down]:max-h-[80vh] data-[swipe-direction=down]:rounded-t-lg data-[swipe-direction=down]:border-t",
            // Swipe/snap offsets are published by Base UI as CSS variables.
            "[transform:translateY(calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y,0px)))] transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] data-[ending-style]:[transform:translateY(100%)] data-[starting-style]:[transform:translateY(100%)] data-[swiping]:duration-0 motion-reduce:transition-none",
            className,
          )}
          {...props}
        >
          {handle ??
            (showHandle && (
              <div
                data-slot="drawer-handle"
                className="bg-muted mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full group-data-[swipe-direction=down]/drawer-content:block"
              />
            ))}
          {/*
            Base UI's `Drawer.Content`: a MOUSE press inside it never starts a
            swipe (`DrawerViewport` returns early on `[data-drawer-content]`).
            Without it, a press on any non-button element in the sheet — a cmdk
            item (`role=option`), a plain clickable div — started a swipe whose
            `setPointerCapture` on the popup delivered the release and the
            click to the popup, so the element never got its click. Touch
            swipes are unaffected (separate path, no capture); the grab bar /
            `handle` above stays a mouse swipe origin. `display: contents` keeps
            the children direct flex items of the popup for layout.
          */}
          <DrawerPrimitive.Content data-slot="drawer-body" className="contents">
            {children}
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPortal>
  )
}

function DrawerHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        "flex flex-col gap-0.5 p-4 group-data-[swipe-direction=down]/drawer-content:text-center md:gap-1.5 md:text-left",
        className,
      )}
      {...props}
    />
  )
}

function DrawerFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function DrawerTitle({ className, ...props }: DrawerPrimitive.Title.Props) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  )
}

function DrawerDescription({
  className,
  ...props
}: DrawerPrimitive.Description.Props) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
}
